import en from './messages/en.json';
import rw from './messages/rw.json';

/**
 * Lightweight i18n for the learner-facing UI.
 * - Kinyarwanda (rw) is the DEFAULT; English (en) is the alternative.
 * - The locale lives in a cookie so server and client render the same words.
 * - Wording is admin-managed: the DB (via the API's /i18n/messages) overrides
 *   the bundled JSON dictionaries, which remain the seed + offline fallback.
 * - Course/lesson CONTENT stays as authored (it teaches English); only the
 *   interface chrome is translated. The admin panel stays English.
 */

export const LOCALES = ['rw', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'rw';
export const LOCALE_COOKIE = 'vn-locale';

/** Nested dictionary shape. rw may omit keys (missing = fall back to en). */
export type Dict = { [segment: string]: string | Dict };
export type Messages = typeof en;

const DICTIONARIES: Record<Locale, Dict> = { en: en as Dict, rw: rw as Dict };

/** Flat admin-edited wording per locale, fetched from the API at runtime. */
export type UiOverrides = Partial<Record<Locale, Record<string, string>>>;

export function isLocale(value: unknown): value is Locale {
  return value === 'rw' || value === 'en';
}

function resolve(dict: Dict, key: string): string | undefined {
  const value = key.split('.').reduce<unknown>((node, part) => {
    if (node && typeof node === 'object') return (node as Record<string, unknown>)[part];
    return undefined;
  }, dict);
  return typeof value === 'string' ? value : undefined;
}

/**
 * Dot-path lookup with {param} interpolation.
 * Order: admin override (locale) → bundled (locale) → admin override (en) →
 * bundled (en) → the key itself. Admin edits always win over shipped JSON.
 */
export function translate(
  locale: Locale,
  key: string,
  params?: Record<string, string | number>,
  overrides?: UiOverrides,
): string {
  const text =
    overrides?.[locale]?.[key] ??
    resolve(DICTIONARIES[locale], key) ??
    overrides?.en?.[key] ??
    resolve(DICTIONARIES.en, key) ??
    key;

  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    params[name] !== undefined ? String(params[name]) : match,
  );
}

export type Translator = (key: string, params?: Record<string, string | number>) => string;

export function makeT(locale: Locale, overrides?: UiOverrides): Translator {
  return (key, params) => translate(locale, key, params, overrides);
}

/** Flatten a nested dictionary to dot-path keys ("header.myLearning"). */
export function flattenMessages(dict: Dict, prefix = ''): Record<string, string> {
  const flat: Record<string, string> = {};
  for (const [part, value] of Object.entries(dict)) {
    const key = prefix ? `${prefix}.${part}` : part;
    if (typeof value === 'string') flat[key] = value;
    else Object.assign(flat, flattenMessages(value, key));
  }
  return flat;
}

/** The bundled seed as flat entries — what the admin "Sync from code" pushes. */
export function bundledSeedEntries(): { key: string; en: string; rw?: string }[] {
  const flatEn = flattenMessages(DICTIONARIES.en);
  const flatRw = flattenMessages(DICTIONARIES.rw);
  return Object.entries(flatEn).map(([key, value]) => ({
    key,
    en: value,
    ...(flatRw[key] !== undefined ? { rw: flatRw[key] } : {}),
  }));
}
